import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  ChartBarIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  Squares2X2Icon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

// Agent metadata
const AGENT_INFO = {
  'valuation_chat_agent': {
    name: 'Valuation Specialist',
    icon: ChartBarIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-500'
  },
  'continuity_guardian_agent': {
    name: 'Continuity Specialist',
    icon: ShieldCheckIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-500'
  },
  'content_creation_copilot_agent': {
    name: 'Content Co-Pilot',
    icon: DocumentTextIcon,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-500'
  },
  'succession_dashboard_agent': {
    name: 'Succession Dashboard',
    icon: Squares2X2Icon,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-500'
  }
};

/**
 * AgentAnalytics - Usage statistics and insights
 *
 * Features:
 * - Total conversations by agent
 * - Usage over time
 * - Most active agent
 * - Average conversation duration (mock)
 */
const AgentAnalytics = ({ conversations = [], user }) => {
  const intl = useIntl();
  // Calculate analytics
  const analytics = useMemo(() => {
    const stats = {
      totalConversations: conversations.length,
      byAgent: {},
      thisWeek: 0,
      thisMonth: 0,
      avgPerWeek: 0
    };

    const now = new Date();
    const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);

    conversations.forEach(conv => {
      // Count by agent
      if (!stats.byAgent[conv.agentType]) {
        stats.byAgent[conv.agentType] = {
          count: 0,
          lastUsed: conv.lastActivity
        };
      }
      stats.byAgent[conv.agentType].count++;

      // Update last used
      if (new Date(conv.lastActivity) > new Date(stats.byAgent[conv.agentType].lastUsed)) {
        stats.byAgent[conv.agentType].lastUsed = conv.lastActivity;
      }

      // Count this week/month
      const startedAt = new Date(conv.startedAt);
      if (startedAt >= oneWeekAgo) stats.thisWeek++;
      if (startedAt >= oneMonthAgo) stats.thisMonth++;
    });

    // Calculate average per week (over last 4 weeks)
    stats.avgPerWeek = Math.round(stats.thisMonth / 4);

    // Find most used agent
    let mostUsed = null;
    let maxCount = 0;
    Object.entries(stats.byAgent).forEach(([agentType, data]) => {
      if (data.count > maxCount) {
        maxCount = data.count;
        mostUsed = agentType;
      }
    });
    stats.mostUsedAgent = mostUsed;

    return stats;
  }, [conversations]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Calculate percentage for agent usage
  const getAgentPercentage = (count) => {
    if (analytics.totalConversations === 0) return 0;
    return Math.round((count / analytics.totalConversations) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Conversations */}
        <div className="bg-white rounded-lg border border-succession-border p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-sm font-medium text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.total_conversations' })}
            </h3>
          </div>
          <p className="text-3xl font-bold text-succession-text-primary">
            {analytics.totalConversations}
          </p>
        </div>

        {/* This Week */}
        <div className="bg-white rounded-lg border border-succession-border p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <CalendarIcon className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-sm font-medium text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.this_week' })}
            </h3>
          </div>
          <p className="text-3xl font-bold text-succession-text-primary">
            {analytics.thisWeek}
          </p>
        </div>

        {/* This Month */}
        <div className="bg-white rounded-lg border border-succession-border p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <ClockIcon className="h-5 w-5 text-purple-600" />
            </div>
            <h3 className="text-sm font-medium text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.this_month' })}
            </h3>
          </div>
          <p className="text-3xl font-bold text-succession-text-primary">
            {analytics.thisMonth}
          </p>
        </div>

        {/* Average Per Week */}
        <div className="bg-white rounded-lg border border-succession-border p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center">
              <ChartBarIcon className="h-5 w-5 text-cyan-600" />
            </div>
            <h3 className="text-sm font-medium text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.avg_per_week' })}
            </h3>
          </div>
          <p className="text-3xl font-bold text-succession-text-primary">
            {analytics.avgPerWeek}
          </p>
        </div>
      </div>

      {/* Agent Usage Breakdown */}
      <div className="bg-white rounded-lg border border-succession-border p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-succession-text-primary mb-6">
          {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.agent_usage_breakdown' })}
        </h2>

        {Object.keys(analytics.byAgent).length === 0 ? (
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-succession-text-muted mx-auto mb-4" />
            <p className="text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.no_data' })}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(analytics.byAgent)
              .sort((a, b) => b[1].count - a[1].count)
              .map(([agentType, data]) => {
                const agentInfo = AGENT_INFO[agentType] || {};
                const Icon = agentInfo.icon || ChatBubbleLeftRightIcon;
                const percentage = getAgentPercentage(data.count);
                const isMostUsed = agentType === analytics.mostUsedAgent;

                return (
                  <div key={agentType} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${agentInfo.bgColor} rounded-lg flex items-center justify-center`}>
                          <Icon className={`h-5 w-5 ${agentInfo.color}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-succession-text-primary">
                              {agentInfo.name || agentType}
                            </span>
                            {isMostUsed && (
                              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                                {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.most_used' })}
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-succession-text-secondary">
                            {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.last_used' })} {formatDate(data.lastUsed)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-succession-text-primary">
                          {data.count}
                        </div>
                        <div className="text-sm text-succession-text-secondary">
                          {percentage}%
                        </div>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="h-2 bg-succession-bg-secondary rounded-full overflow-hidden">
                      <div
                        className={`h-full ${agentInfo.bgColor} ${agentInfo.borderColor} border-l-4 transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Insights */}
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border border-blue-200 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
            <ChartBarIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.usage_insights' })}
            </h3>
            <div className="space-y-2 text-sm text-gray-700">
              {analytics.totalConversations === 0 ? (
                <p>{intl.formatMessage({ id: 'succession.agent_system.agent_analytics.start_using' })}</p>
              ) : (
                <>
                  <p>
                    • {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.conversations_count' }, { count: <strong>{analytics.totalConversations}</strong> })}
                  </p>
                  {analytics.mostUsedAgent && (
                    <p>
                      • {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.most_frequent_agent' }, { agent: <strong>{AGENT_INFO[analytics.mostUsedAgent]?.name}</strong> })}
                    </p>
                  )}
                  {analytics.thisWeek > 0 && (
                    <p>
                      • {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.active_conversations' }, { count: <strong>{analytics.thisWeek}</strong> })}
                    </p>
                  )}
                  {analytics.avgPerWeek > 0 && (
                    <p>
                      • {intl.formatMessage({ id: 'succession.agent_system.agent_analytics.avg_conversations' }, { count: <strong>{analytics.avgPerWeek}</strong> })}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AgentAnalytics.propTypes = {
  conversations: PropTypes.array,
  user: PropTypes.object
};

export default AgentAnalytics;
